import { useState } from 'react';

import {
  AdminApiError,
  previewTransfer,
  transferAccount,
  type TransferPreview,
  type TransferResult,
} from '@/lib/adminApi';

import { MIN_REASON_LENGTH } from './ResetPasswordDialog';

interface TransferAccountDialogProps {
  userId: string;
  sourceEmail?: string;
  onClose: () => void;
  onSuccess: (result: TransferResult) => void;
}

/**
 * Merge this account into another (ClickUp 869f4a4a0) — two steps, like the
 * delete flow's preview/confirm: first the server says what WILL move
 * (organizations, workspaces with their credits, the per-table row counts,
 * and what the target already holds), then the agent types the source's
 * email to confirm and the write runs. The source account is erased once
 * its ownership has moved, so the confirm is the same typed-email ritual the
 * delete uses — this is a deletion with a forwarding address.
 */
export function TransferAccountDialog({ userId, sourceEmail, onClose, onSuccess }: TransferAccountDialogProps) {
  const [targetId, setTargetId] = useState('');
  const [reason, setReason] = useState('');
  const [confirm, setConfirm] = useState('');
  const [preview, setPreview] = useState<TransferPreview | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const trimmedReason = reason.trim();
  const trimmedTarget = targetId.trim();
  const canPreview = trimmedReason.length >= MIN_REASON_LENGTH && trimmedTarget.length > 0 && !busy;
  const confirmMatches =
    Boolean(sourceEmail) && confirm.trim().toLowerCase() === (sourceEmail ?? '').toLowerCase();
  const canTransfer =
    preview !== null && preview.blocked_by_sole_admin_of_org === null && confirmMatches && !busy;

  async function handlePreview() {
    if (!canPreview) return;
    setBusy(true);
    setError(null);
    try {
      setPreview(await previewTransfer(userId, trimmedTarget, trimmedReason));
    } catch (err) {
      setError(err instanceof AdminApiError ? err.message : 'Could not preview the transfer.');
    } finally {
      setBusy(false);
    }
  }

  async function handleTransfer() {
    if (!canTransfer) return;
    setBusy(true);
    setError(null);
    try {
      const result = await transferAccount(userId, trimmedTarget, trimmedReason, confirm.trim());
      onSuccess(result);
      onClose();
    } catch (err) {
      setError(err instanceof AdminApiError ? err.message : 'Could not transfer the account.');
      setBusy(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Transfer account"
      className="fixed inset-0 z-popover flex items-center justify-center bg-scrim/40 px-4"
    >
      <div className="max-h-screen w-full max-w-lg overflow-y-auto rounded-panel border border-border bg-surface-field p-6">
        <h2 className="text-h3 font-semibold text-fg-primary">Transfer account</h2>
        <p className="mt-2 text-body text-fg-secondary">
          Everything this account owns — organizations, workspaces, workflows, runs, credits and
          the subscription that follows the organization — moves to the target account. This
          account is then erased and can no longer sign in.
        </p>

        <label className="mt-4 block text-meta font-medium text-fg-secondary" htmlFor="transfer-target">
          Target user id (the account that keeps everything)
        </label>
        <input
          id="transfer-target"
          type="text"
          value={targetId}
          onChange={(event) => {
            setTargetId(event.target.value);
            setPreview(null);
          }}
          disabled={busy}
          autoComplete="off"
          className="mt-1 w-full rounded-card border border-border bg-background px-3 py-2 font-mono text-body text-fg-primary focus-visible:border-border-focus"
        />

        <label className="mt-4 block text-meta font-medium text-fg-secondary" htmlFor="transfer-reason">
          Reason (required, at least {MIN_REASON_LENGTH} characters)
        </label>
        <textarea
          id="transfer-reason"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          rows={3}
          disabled={busy}
          className="mt-1 w-full rounded-card border border-border bg-background px-3 py-2 text-body text-fg-primary focus-visible:border-border-focus"
        />

        {preview && <PreviewPanel preview={preview} />}

        {preview && (
          <>
            <label className="mt-4 block text-meta font-medium text-fg-secondary" htmlFor="transfer-confirm">
              Type the source account&apos;s email to confirm ({sourceEmail ?? 'unknown'})
            </label>
            <input
              id="transfer-confirm"
              type="text"
              value={confirm}
              onChange={(event) => setConfirm(event.target.value)}
              disabled={busy}
              autoComplete="off"
              className="mt-1 w-full rounded-card border border-border bg-background px-3 py-2 text-body text-fg-primary focus-visible:border-border-focus"
            />
          </>
        )}

        {error && (
          <p
            role="alert"
            className="mt-3 rounded-card border border-destructive-border bg-destructive-soft px-3 py-2 text-meta text-destructive-soft-foreground"
          >
            {error}
          </p>
        )}

        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded-card px-4 py-2 text-body font-medium text-fg-secondary hover:bg-state-hover disabled:cursor-not-allowed disabled:opacity-50"
          >
            Cancel
          </button>
          {preview === null ? (
            <button
              type="button"
              onClick={handlePreview}
              disabled={!canPreview}
              className="rounded-card bg-primary px-4 py-2 text-body font-medium text-primary-foreground hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
            >
              {busy ? 'Loading…' : 'Preview what moves'}
            </button>
          ) : (
            <button
              type="button"
              onClick={handleTransfer}
              disabled={!canTransfer}
              className="rounded-card bg-destructive px-4 py-2 text-body font-medium text-destructive-foreground hover:bg-destructive-hover disabled:cursor-not-allowed disabled:opacity-50"
            >
              {busy ? 'Transferring…' : 'Transfer and erase this account'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function PreviewPanel({ preview }: { preview: TransferPreview }) {
  return (
    <div className="mt-4 rounded-card border border-border bg-surface-muted p-3 text-body" data-testid="transfer-preview">
      <div className="text-meta font-medium text-fg-secondary">What moves</div>
      <p className="mt-1 text-fg-primary">
        To <span className="font-mono text-micro">{preview.target.profile_id}</span>
        {preview.target.email ? ` (${preview.target.email})` : ''}: {preview.source.pipelines} workflow
        {preview.source.pipelines === 1 ? '' : 's'}, {preview.source.runs} run
        {preview.source.runs === 1 ? '' : 's'}, {preview.source.ledger_rows} ledger line
        {preview.source.ledger_rows === 1 ? '' : 's'}.
      </p>

      {preview.organizations.length > 0 && (
        <ul className="mt-2 list-disc pl-5 text-fg-primary">
          {preview.organizations.map((org) => (
            <li key={org.organization_id}>
              Organization <strong>{org.name ?? org.organization_id}</strong> as {org.role ?? '—'}
              {org.plan_name ? `, plan ${org.plan_name}` : ''}, {org.member_count} member
              {org.member_count === 1 ? '' : 's'}
              {org.target_already_member_as
                ? ` — target already a ${org.target_already_member_as} there (their row is kept)`
                : ''}
            </li>
          ))}
        </ul>
      )}

      {preview.workspaces.length > 0 && (
        <ul className="mt-2 list-disc pl-5 text-fg-primary">
          {preview.workspaces.map((ws) => (
            <li key={ws.workspace_id}>
              Workspace <strong>{ws.name ?? ws.workspace_id}</strong> as {ws.role ?? '—'}
              {ws.credits_remaining !== null ? `, ${ws.credits_remaining} credits remaining` : ''}
              {ws.target_already_member_as ? ` — target already a ${ws.target_already_member_as} there` : ''}
            </li>
          ))}
        </ul>
      )}

      {preview.organizations.length === 0 && preview.workspaces.length === 0 && (
        <p className="mt-2 text-fg-secondary">No organization or workspace membership to move.</p>
      )}

      <details className="mt-2">
        <summary className="cursor-pointer text-meta text-fg-secondary">
          Rows re-pointed, by table ({preview.rows.length})
        </summary>
        <ul className="mt-1 font-mono text-micro text-fg-tertiary">
          {preview.rows.map((row) => (
            <li key={`${row.table}.${row.column}`}>
              {row.table}.{row.column}: {row.rows}
              {row.duplicates_dropped > 0 ? ` (${row.duplicates_dropped} dropped as duplicates)` : ''}
            </li>
          ))}
        </ul>
      </details>

      {preview.blocked_by_sole_admin_of_org && (
        <p
          role="alert"
          className="mt-3 rounded-card border border-destructive-border bg-destructive-soft px-3 py-2 text-meta text-destructive-soft-foreground"
        >
          Blocked: the source is the only admin of organization {preview.blocked_by_sole_admin_of_org},
          where the target is a non-admin member. Make the target an admin there first.
        </p>
      )}
    </div>
  );
}
